import React, { useState } from 'react';
import { Modal, Form, Input, Button, Upload } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { getConfig } from '../../../../config';
import { dietTypeService } from '../../../../services/settings-services';
import { dietTypeActions } from '../../../../store/slices/settings-slices';

const DietTypeCreateModal = ({ isOpen, setIsOpen }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { cityCode } = getConfig();
  const [uploadedImage, setUploadedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      
      // Append image if uploaded
      if (uploadedImage) {
        formData.append('file', uploadedImage);
      }
      
      // Append name as JSON
      formData.append('name', JSON.stringify({
        en: values.en,
        ru: values.ru,
        ar: values.ar,
        ge: values.ge,
      }));

      await dietTypeService.createDietType(formData);
      dispatch(dietTypeActions.changeTrigger());
      toast.success(t('Diet type created successfully'));
      setIsOpen(false);
      form.resetFields();
      setUploadedImage(null);
      setPreviewImage(null);
    } catch (error) {
      console.error('Error creating diet type:', error);
      toast.error(t('Error creating diet type'));
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (info) => {
    const file = info.file.originFileObj || info.file;
    if (file) {
      setUploadedImage(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => setPreviewImage(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setUploadedImage(null);
    setPreviewImage(null);
  };

  const handleCancel = () => {
    setIsOpen(false);
    form.resetFields();
    setUploadedImage(null);
    setPreviewImage(null);
  };

  return (
    <Modal
      title={t('Create New Diet Type')}
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
      maskClosable={!loading}
      closable={!loading}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        data-lpignore="true"
        data-form-type="other"
      >
        <Form.Item
          label={t('Diet Type Icon')}
          style={{ marginBottom: '1.5rem' }}
        >
          <Upload
            listType="picture-card"
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleImageChange}
            accept="image/*"
          >
            {previewImage ? (
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <img
                  src={previewImage}
                  alt="preview"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <Button
                  type="text"
                  danger
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage();
                  }}
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    background: 'rgba(255, 255, 255, 0.9)',
                  }}
                >
                  ×
                </Button>
              </div>
            ) : (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>{t('Upload')}</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item
          label={`${t('Diet Type Name')} (English)`}
          name="en"
          rules={[
            {
              required: true,
              message: t('This field is required / Это поле обязательно / ეს ველი აუცილებელია'),
            },
          ]}
        >
          <Input placeholder={t('Enter diet type name in English')} />
        </Form.Item>

        <Form.Item
          label={`${t('Diet Type Name')} (Русский)`}
          name="ru"
          rules={[
            {
              required: true,
              message: t('This field is required / Это поле обязательно / ეს ველი აუცილებელია'),
            },
          ]}
        >
          <Input placeholder={t('Enter diet type name in Russian')} />
        </Form.Item>

        {cityCode === 'UAE' ? (
          <Form.Item
            label={`${t('Diet Type Name')} (العربية)`}
            name="ar"
            rules={[
              {
                required: true,
                message: t('This field is required / Это поле обязательно / ეს ველი აუცილებელია'),
              },
            ]}
          >
            <Input placeholder={t('Enter diet type name in Arabic')} />
          </Form.Item>
        ) : (
          <Form.Item
            label={`${t('Diet Type Name')} (ქართული)`}
            name="ge"
            rules={[
              {
                required: true,
                message: t('This field is required / Это поле обязательно / ეს ველი აუცილებელია'),
              },
            ]}
          >
            <Input placeholder={t('Enter diet type name in Georgian')} />
          </Form.Item>
        )}

        <Form.Item style={{ marginBottom: 0, marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <Button onClick={handleCancel} disabled={loading}>
              {t('Cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={loading} disabled={loading}>
              {t('Create')}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export { DietTypeCreateModal };

